import { useEffect, useState } from 'react';
import { Alert, Button, ConfigProvider, Modal, Form, Divider, Tabs } from 'antd';
import { CustomerInfoFields } from './CustomerInfoFields';
import { DeliveryRecipientFields } from './DeliveryRecipientFields';
import { InvoiceSection } from './InvoiceSection';
import { CustomerTypeToggle } from './CustomerTypeToggle';
import { customerDialogValidationFields } from './customerFormSchema';
import type { FormInstance } from 'antd';
import type { UserType } from '@/pages/POS/types';
import type { CustomerEditAbilities } from '@/utils/customerFieldPermissions';
import {
  customerValidationPolicyProblem,
  customerValidationRequirements,
  type CustomerValidationPolicy,
} from '@/pages/POS/utils/customerCheckout';
import './CustomerSidebar.css';

export interface CustomerDialogProps {
  open: boolean;
  mode: 'add' | 'edit';
  form: FormInstance;
  userType: UserType;
  validationPolicy: CustomerValidationPolicy;
  onRetryValidationPolicy: () => void;
  onUserTypeChange: (v: UserType) => void;
  onClose: () => void;
  onSaved: () => void;
  cccdMasked?: boolean;
  onRevealCccd?: () => void;
  /**
   * TCR-1146: `onSaved` giờ có thể gọi mạng (ghi hồ sơ khách). Khoá nút OK
   * trong lúc chờ để thu ngân không bấm "Xác nhận" hai lần thành hai request.
   */
  confirmLoading?: boolean;
  /**
   * TCR-1953 — quyền sửa HỒ SƠ khách của người đang đăng nhập.
   *
   * Trước fix, dialog này không hỏi quyền gì cả: NVKD bị bỏ tick "Sửa khách hàng" vẫn gõ đè được
   * toàn bộ thông tin khách, bấm Xác nhận thì BE trả 403 và FE nuốt thành toast "Thông tin vẫn
   * được dùng cho đơn này" — người dùng tưởng đã sửa xong.
   *
   * Lưu ý phạm vi: chỉ khối THÔNG TIN KHÁCH HÀNG và THÔNG TIN XUẤT HOÁ ĐƠN bị khoá (đó là hồ sơ
   * khách). Khối THÔNG TIN GIAO NHẬN CỐ Ý để mở — nó là người nhận của ĐƠN này, khoá nó là chặn
   * NVKD giao hàng tới địa chỉ khác cho một đơn lẻ. Phần ghi ngược vào sổ địa chỉ khách mới là
   * chỗ cần quyền, và nó được chặn ở `CustomerSidebar.handleDialogSaved`.
   */
  abilities: CustomerEditAbilities;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontWeight: 700,
        fontSize: 11,
        color: '#555',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        margin: '4px 0 8px',
      }}
    >
      {children}
    </div>
  );
}

export function CustomerDialog({
  open,
  mode,
  form,
  userType,
  validationPolicy,
  onRetryValidationPolicy,
  onUserTypeChange,
  onClose,
  onSaved,
  cccdMasked,
  onRevealCccd,
  confirmLoading = false,
  abilities,
}: CustomerDialogProps) {
  const validationRequirements = customerValidationRequirements(validationPolicy);
  const validationPolicyProblem = customerValidationPolicyProblem(validationPolicy);
  const cityId = Form.useWatch('customer_city_id', form) as string | undefined;
  const deliveryCityId = Form.useWatch('delivery_city_id', form) as string | undefined;
  // Đơn lấy tại chi nhánh / lưu kho vẫn HIỆN form người nhận để xem/sửa, nhưng
  // không bắt điền — chỉ đơn giao hàng mới cần. `preserve: true` là bắt buộc:
  // `Form.Item name="receive_method"` do `FulfillmentFormSection` sở hữu và chỉ
  // mount ở nơi khối vận chuyển hiển thị, không phải trong Modal này.
  const receiveMethod =
    (Form.useWatch('receive_method', { form, preserve: true }) as
      | 'delivery'
      | 'pickup'
      | 'vault'
      | undefined) ?? 'delivery';
  // TCR-1953 — chỉ khoá ở chế độ SỬA khách đã có. Chế độ 'add' là TẠO khách mới, đã được gác
  // bằng `customers.create` ngay ở nút "+" mở dialog (TCR-1726) — khoá thêm ở đây là FE chặt hơn
  // BE, vai trò có quyền tạo sẽ thấy form mờ đi không hiểu vì sao.
  const profileLocked = mode === 'edit' && !abilities.canEditAll;
  // TCR-1953 (QA reopen) — mã KH AMIS, field hiển thị-only ghi lúc chọn khách (không có Form.Item
  // nên BẮT BUỘC `preserve: true`). `undefined` = khách không đến từ ô tìm kiếm ⇒ không hiện dòng.
  const amisCode = Form.useWatch('_amisCode', { form, preserve: true }) as string | undefined;
  const [activeTab, setActiveTab] = useState('fulfillment');
  const [tabErrors, setTabErrors] = useState<{ fulfillment: boolean; invoice: boolean }>({
    fulfillment: false,
    invoice: false,
  });

  useEffect(() => {
    if (open) {
      setTabErrors({ fulfillment: false, invoice: false });
    }
  }, [open]);

  async function handleSave() {
    if (!validationRequirements.isResolved) return;

    // Chỉ validate các field thuộc dialog (KHÔNG gồm care_staff_id — đã chuyển ra
    // sidebar). Ô chọn chi nhánh (`pickup_store_id`) đã chuyển ra thanh phương
    // thức nhận hàng ở đáy màn hình, nên dialog không còn validate nó (cổng của
    // nó nằm ở `missingCustomerFields`). Bộ field người nhận vẫn chỉ bắt buộc
    // với đơn giao hàng.
    const fulfillmentFields = receiveMethod === 'delivery'
      ? ['delivery_name', 'delivery_phone']
      : [];
    const invoiceFields = ['invoice_name', 'invoice_tax_id', 'invoice_address', 'invoice_email', 'buyer_name', 'buyer_cccd'];
    const dialogFields = customerDialogValidationFields(userType, receiveMethod);
    try {
      await form.validateFields(dialogFields);
      setTabErrors({ fulfillment: false, invoice: false });
      onSaved();
    } catch (e) {
      // Đánh dấu tab nào đang có lỗi để bôi đỏ + nhảy tới tab lỗi đầu tiên.
      const errorFields =
        (e as { errorFields?: { name: (string | number)[] }[] }).errorFields ?? [];
      const erroredNames = new Set(errorFields.map((f) => String(f.name[0])));
      const fulfillmentErr = fulfillmentFields.some((n) => erroredNames.has(n));
      const invoiceErr = invoiceFields.some((n) => erroredNames.has(n));
      setTabErrors({ fulfillment: fulfillmentErr, invoice: invoiceErr });
      if (fulfillmentErr) setActiveTab('fulfillment');
      else if (invoiceErr) setActiveTab('invoice');
      // Lỗi ở phần Thông tin khách hàng (luôn hiển thị) tự báo inline.
    }
  }

  const errorLabel = (text: string, hasError: boolean) => (
    <span style={{ color: hasError ? '#ff4d4f' : undefined, fontWeight: hasError ? 700 : undefined }}>
      {text}
    </span>
  );

  return (
    <Modal
      open={open}
      title={mode === 'add' ? 'Thêm khách hàng' : 'Thông tin khách hàng'}
      /* Huỷ chỉ đóng dialog; việc hoàn nguyên dữ liệu do `CustomerSidebar` làm
         trong `onClose`. Dialog KHÔNG được tự chụp ảnh form lúc `open` thành
         true: hai đường mở nó — chọn khách ở ô tìm kiếm và bấm "+" thêm khách —
         đều ghi vào form RỒI mới mở, nên ảnh chụp đó đã là trạng thái SAU khi
         ghi và Huỷ khôi phục lại đúng thứ vừa ghi. Chỉ nơi gọi mới biết cái gì
         có trước lần sửa của chính nó. */
      onCancel={onClose}
      onOk={handleSave}
      okText={mode === 'add' ? 'Lưu' : 'Xác nhận'}
      cancelText="Huỷ"
      width={720}
      destroyOnClose={false}
      maskClosable={false}
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: !validationRequirements.isResolved }}
    >
      <Form form={form} component={false}>
        {validationPolicyProblem && (
          <Alert
            type={validationRequirements.isError ? 'error' : 'warning'}
            showIcon
            message={validationPolicyProblem}
            action={
              validationRequirements.isError ? (
                <Button size="small" onClick={onRetryValidationPolicy}>
                  Thử lại
                </Button>
              ) : undefined
            }
            style={{ marginBottom: 12 }}
          />
        )}
        {/* `receive_method` / `pickup_store_id` / `shipping_*` / `package_*` không
            còn đăng ký ở dialog này — thanh "Phương thức nhận hàng" 4 nút neo đáy
            màn hình (`FulfillmentFormSection`) là nơi duy nhất giữ nhóm field đó. */}

        <SectionTitle>THÔNG TIN KHÁCH HÀNG</SectionTitle>

        {mode === 'edit' && amisCode !== undefined && (
          <div data-testid="customer-dialog-amis-code" style={{ fontSize: 13, marginBottom: 8 }}>
            Mã KH AMIS: <strong>{amisCode || '—'}</strong>
          </div>
        )}

        <div style={{ marginBottom: 8 }}>
          {/* Xem/sửa khách đã có thì khoá loại khách: đổi loại xoá trắng bộ field
              định danh của loại cũ, mở form ra xem mà lỡ tay là mất CCCD/MST.
              Thêm khách mới vẫn phải chọn được. */}
          <CustomerTypeToggle
            value={userType}
            onChange={onUserTypeChange}
            disabled={mode === 'edit'}
          />
        </div>

        {/* TCR-1953 — khoá cả khối bằng `ConfigProvider componentDisabled` thay vì rải `disabled`
            xuống từng ô trong `CustomerInfoFields`: khối này có hơn chục field (họ tên, SĐT, MST,
            CCCD, ngày sinh, tỉnh/phường…) và sót một ô là thủng đúng cái bug đang sửa. */}
        <ConfigProvider componentDisabled={profileLocked}>
          <div className="cust-dialog-grid">
            <CustomerInfoFields
              form={form}
              userType={userType}
              personalCccdRequired={validationRequirements.personalCccdRequired}
              cityId={cityId}
              cccdMasked={cccdMasked}
              onRevealCccd={onRevealCccd}
              phoneDisabled={profileLocked ? !abilities.canEditPhone : undefined}
            />
          </div>
        </ConfigProvider>

        <Divider style={{ margin: '12px 0 4px' }} />

        {/* Giao nhận / Xuất hoá đơn tách thành 2 tab. `forceRender` giữ Form.Item
            của cả 2 tab luôn được đăng ký (validate + submit + copy effect vẫn
            chạy dù tab đang ẩn). Tab 1 luôn là thông tin giao nhận — ô chọn chi
            nhánh (`pickup_store_id`) đã chuyển ra thanh phương thức nhận hàng ở
            đáy màn hình, không còn nhánh theo `receive_method` ở đây nữa. */}
        <Tabs
          activeKey={activeTab}
          onChange={(k) => {
            setActiveTab(k);
            // Xem tab nào thì bỏ đánh dấu đỏ tab đó.
            setTabErrors((prev) => ({ ...prev, [k]: false }));
          }}
          items={[
            {
              key: 'fulfillment',
              label: errorLabel('THÔNG TIN GIAO NHẬN', tabErrors.fulfillment),
              forceRender: true,
              children: (
                <div className="cust-dialog-grid">
                  <DeliveryRecipientFields
                    form={form}
                    cityId={deliveryCityId}
                    cccdMasked={cccdMasked}
                    onRevealCccd={onRevealCccd}
                    canEditAddressBook={abilities.canEditDeliveryAddress}
                  />
                </div>
              ),
            },
            {
              key: 'invoice',
              label: errorLabel('THÔNG TIN XUẤT HOÁ ĐƠN', tabErrors.invoice),
              forceRender: true,
              children: (
                /* TCR-1952/1953 — thông tin xuất hoá đơn (tên công ty, MST, địa chỉ HĐ, người
                   mua/CCCD) là hồ sơ khách nên khoá chung; RIÊNG ô "Email nhận hoá đơn" mở theo
                   `customers.invoice_email.update`. `undefined` = theo ngữ cảnh (không khoá). */
                <ConfigProvider componentDisabled={profileLocked}>
                  <div className="cust-dialog-grid">
                    <InvoiceSection
                      form={form}
                      userType={userType}
                      customerMode
                      personalInvoiceOptional={validationRequirements.personalInvoiceOptional}
                      cccdMasked={cccdMasked}
                      onRevealCccd={onRevealCccd}
                      invoiceEmailDisabled={
                        profileLocked ? !abilities.canEditInvoiceEmail : undefined
                      }
                    />
                  </div>
                </ConfigProvider>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
}
